/* Illustrative, photo-based makeup preview. The source pixels and lip shape remain intact. */
(() => {
  if (window.BPLipRenderer) return;

  const instances = new WeakMap();
  const MAX_SIZE = 768;
  const clamp = (n, low = 0, high = 1) => Math.max(low, Math.min(high, n));
  const smooth = (a, b, value) => {
    const t = clamp((value - a) / (b - a));
    return t * t * (3 - 2 * t);
  };

  // Normalized contours traced just inside the vermilion in the three aligned photographs.
  // The inner upper/lower contours leave the natural mouth seam completely untreated.
  const CONTOURS = {
    upperTop: [[.161,.430],[.180,.411],[.215,.386],[.270,.344],[.331,.304],[.394,.278],[.447,.262],[.471,.274],[.489,.284],[.500,.287],[.511,.284],[.529,.274],[.552,.263],[.602,.273],[.663,.303],[.727,.343],[.784,.386],[.825,.419],[.842,.431]],
    upperBottom: [[.161,.432],[.188,.430],[.245,.426],[.315,.423],[.395,.421],[.468,.422],[.500,.424],[.558,.424],[.633,.422],[.706,.424],[.779,.428],[.826,.433],[.842,.433]],
    lowerTop: [[.161,.442],[.190,.438],[.248,.434],[.318,.432],[.393,.430],[.472,.429],[.500,.430],[.555,.431],[.634,.431],[.706,.434],[.775,.438],[.822,.441],[.842,.443]],
    lowerBottom: [[.161,.444],[.187,.473],[.223,.507],[.268,.540],[.323,.576],[.385,.606],[.450,.622],[.500,.626],[.552,.622],[.618,.606],[.682,.576],[.737,.540],[.787,.501],[.827,.463],[.842,.445]]
  };

  function sample(points, x) {
    if (x < points[0][0] || x > points[points.length - 1][0]) return null;
    let i = 0;
    while (i < points.length - 2 && points[i + 1][0] < x) i += 1;
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];
    const t = (x - p1[0]) / (p2[0] - p1[0]);
    const m1 = (p2[1] - p0[1]) / (p2[0] - p0[0]);
    const m2 = (p3[1] - p1[1]) / (p3[0] - p1[0]);
    const dx = p2[0] - p1[0];
    const t2 = t * t;
    const t3 = t2 * t;
    return (2*t3 - 3*t2 + 1)*p1[1] + (t3 - 2*t2 + t)*dx*m1 + (-2*t3 + 3*t2)*p2[1] + (t3 - t2)*dx*m2;
  }

  function geometry(width, height) {
    const start = Math.ceil(width * .161);
    const end = Math.floor(width * .842) - 1;
    const columns = new Array(end - start + 1);
    for (let x = start; x <= end; x += 1) {
      const u = (x + .5) / width;
      columns[x - start] = {
        upperTop: sample(CONTOURS.upperTop, u) * height,
        upperBottom: sample(CONTOURS.upperBottom, u) * height,
        lowerTop: sample(CONTOURS.lowerTop, u) * height,
        lowerBottom: sample(CONTOURS.lowerBottom, u) * height
      };
    }
    return { start, end, top: Math.floor(height * .257), bottom: Math.ceil(height * .629), columns };
  }

  function hexColor(value) {
    if (typeof value !== 'string') return null;
    const match = /^#([\da-f]{3}|[\da-f]{6})$/i.exec(value);
    if (!match) return null;
    const hex = match[1].length === 3 ? [...match[1]].map(c => c + c).join('') : match[1];
    return [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16));
  }

  function lipMask(y, top, bottom) {
    if (top === null || bottom === null || bottom <= top) return 0;
    // Feather inward, never across the photographed lip-to-skin boundary.
    return smooth(0, 3, y - top) * smooth(0, 3, bottom - y);
  }

  function sourceMetrics(data, width, area) {
    let sum = 0;
    let count = 0;
    for (let x = area.start; x <= area.end; x += 3) {
      const c = area.columns[x - area.start];
      for (let y = Math.ceil(c.upperTop + 4); y < c.upperBottom - 4; y += 3) {
        const i = (y * width + x) * 4;
        sum += (data[i] * .2126 + data[i + 1] * .7152 + data[i + 2] * .0722);
        count += 1;
      }
      for (let y = Math.ceil(c.lowerTop + 4); y < c.lowerBottom - 4; y += 3) {
        const i = (y * width + x) * 4;
        sum += (data[i] * .2126 + data[i + 1] * .7152 + data[i + 2] * .0722);
        count += 1;
      }
    }
    return count ? sum / count : 125;
  }

  function finishProfile(finish) {
    if (!finish) return null;
    const tint = hexColor(finish.tint);
    return {
      tint,
      opacity: clamp(Number(finish.tintOpacity) || 0, 0, .28),
      sheen: clamp(Number(finish.sheen) || .24, 0, .65),
      glitter: clamp(Number(finish.glitter) || 0, 0, 1),
      glitterColor: hexColor(finish.glitterColor) || [250, 245, 249]
    };
  }

  function baseProfile(base) {
    if (!base || !hexColor(base.shade?.color)) return null;
    const handle = String(base.product?.handle || '');
    if (/tinta-labbra-24milabaci/.test(handle)) return { color: hexColor(base.shade.color), pigment: .80, matte: .56, texture: .73 };
    return { color: hexColor(base.shade.color), pigment: .69, matte: .37, texture: .82 };
  }

  const luminance = (r, g, b) => .2126*r + .7152*g + .0722*b;
  function noise(x, y, salt) {
    let n = Math.imul(x + 71, 374761393) + Math.imul(y + 19, 668265263) + Math.imul(salt + 1, 1442695041);
    n = Math.imul(n ^ (n >>> 13), 1274126177);
    return ((n ^ (n >>> 16)) >>> 0) / 4294967295;
  }

  function renderPhoto(photo, selection) {
    if (!photo.source || !photo.ctx) return;
    const { width, height, original, area, mean } = photo;
    const base = baseProfile(selection.base);
    const liner = hexColor(selection.liner?.shade?.color);
    const lineLum = liner ? luminance(liner[0], liner[1], liner[2]) : 0;
    const finish = finishProfile(selection.finish);
    const channels = base?.color || [0, 0, 0];
    const saturation = base ? (Math.max(...channels) - Math.min(...channels)) / Math.max(1, ...channels) : 1;
    const toneFactor = Math.pow(clamp(mean / 134, .55, 1.15), .38 + .65 * (1 - saturation));
    if (!base && !liner && !finish) {
      photo.ctx.putImageData(photo.source, 0, 0);
      return;
    }
    const result = new ImageData(new Uint8ClampedArray(original), width, height);
    const pixels = result.data;
    const salt = Number(String(selection.finish?.id || selection.base?.id || 0).slice(-6)) || 0;
    for (let x = area.start; x <= area.end; x += 1) {
      const c = area.columns[x - area.start];
      for (let y = Math.max(area.top, Math.floor(c.upperTop - 3)); y <= Math.min(area.bottom, Math.ceil(c.lowerBottom + 3)); y += 1) {
        const upper = y < c.upperBottom + 3 ? lipMask(y, c.upperTop, c.upperBottom) : 0;
        const lower = y > c.lowerTop - 3 ? lipMask(y, c.lowerTop, c.lowerBottom) : 0;
        const mask = Math.max(upper, lower);
        if (mask < .005) continue;
        const i = (y * width + x) * 4;
        const r = original[i], g = original[i + 1], b = original[i + 2];
        const lum = luminance(r, g, b);
        const highlight = clamp((lum - mean * 1.13) / 85);
        const grain = .93 + .07 * noise(x, y, 31);
        let detail = 0;
        if (base || finish) {
          const left = (y * width + Math.max(0, x - 2)) * 4;
          const right = (y * width + Math.min(width - 1, x + 2)) * 4;
          const localAverage = (luminance(original[left], original[left + 1], original[left + 2]) + luminance(original[right], original[right + 1], original[right + 2])) / 2;
          detail = clamp(lum - localAverage, -24, 24);
        }
        let outR = r, outG = g, outB = b;

        if (base) {
          const ratio = Math.pow(clamp(lum / mean, .50, 1.75), base.texture * 1.05);
          const layer = base.pigment * mask * grain;
          const matteDamp = 1 - base.matte * clamp(detail / 18) * .30;
          const texture = detail * (detail < 0 ? .55 : .30);
          outR = r * (1 - layer) + (base.color[0] * toneFactor * ratio * matteDamp + texture) * layer;
          outG = g * (1 - layer) + (base.color[1] * toneFactor * ratio * matteDamp + texture) * layer;
          outB = b * (1 - layer) + (base.color[2] * toneFactor * ratio * matteDamp + texture) * layer;
        }

        if (liner) {
          // Pigment sits just within the outer lip boundary and fades toward the center.
          const edgeDistance = upper ? y - c.upperTop : c.lowerBottom - y;
          const edge = smooth(3, 11, edgeDistance) * Math.exp(-Math.pow((edgeDistance - 12) / 16, 2));
          const pencilGrain = .73 + .27 * noise(x, y, 97);
          const amount = .22 * edge * mask * pencilGrain;
          const mapped = clamp(lum / mean, .65, 1.45);
          const depth = Math.min(1, (lineLum * .78) / Math.max(60, lum));
          outR = outR * (1 - amount) + liner[0] * mapped * depth * amount;
          outG = outG * (1 - amount) + liner[1] * mapped * depth * amount;
          outB = outB * (1 - amount) + liner[2] * mapped * depth * amount;
        }

        if (finish) {
          if (finish.tint && finish.opacity) {
            const tintAmount = finish.opacity * mask;
            outR = outR * (1 - tintAmount) + finish.tint[0] * tintAmount;
            outG = outG * (1 - tintAmount) + finish.tint[1] * tintAmount;
            outB = outB * (1 - tintAmount) + finish.tint[2] * tintAmount;
          }
          // Read the photographed ridges and existing highlights; sheen follows that surface.
          const ridge = clamp(detail / 35);
          const lowerCurve = lower ? smooth(c.lowerTop + 7, c.lowerTop + 35, y) * (1 - smooth(c.lowerBottom - 18, c.lowerBottom - 2, y)) : .50;
          const shine = finish.sheen * mask * (.035 + .42 * ridge + .20 * highlight + .085 * lowerCurve);
          outR += (255 - outR) * shine;
          outG += (255 - outG) * shine;
          outB += (255 - outB) * shine;
          if (finish.glitter > 0 && mask > .72) {
            const chance = noise(x, y, salt);
            const sparkle = chance > 1 - .0055 * finish.glitter ? (.45 + .40 * clamp(ridge + highlight)) * mask : 0;
            if (sparkle) {
              outR += (finish.glitterColor[0] - outR) * sparkle;
              outG += (finish.glitterColor[1] - outG) * sparkle;
              outB += (finish.glitterColor[2] - outB) * sparkle;
            }
          }
        }
        pixels[i] = outR;
        pixels[i + 1] = outG;
        pixels[i + 2] = outB;
      }
    }
    photo.ctx.putImageData(result, 0, 0);
  }

  function mount(container, options = {}) {
    if (!container || typeof container.replaceChildren !== 'function') return { update() {}, destroy() {} };
    if (instances.has(container)) return instances.get(container).api;
    const sources = Array.isArray(options.sources) ? options.sources.slice(0, 3) : [];
    const labels = Array.isArray(options.labels) ? options.labels : [];
    const locale = document.documentElement.lang?.toLowerCase() || 'it';
    const unavailableText = locale.startsWith('it') ? 'anteprima colore non disponibile' : 'colour preview unavailable';
    const enlargeText = locale.startsWith('it') ? 'Ingrandisci' : 'Enlarge';
    const closeText = locale.startsWith('it') ? 'Chiudi' : 'Close';
    const photos = [];
    let destroyed = false;
    let visible = false;
    let frame = 0;
    let selection = { base: null, liner: null, finish: null };
    let selectionKey = null;
    let activePhoto = null;
    let returnFocus = null;
    let observer = null;
    container.classList.add('bp-lip-renderer');
    const fragment = document.createDocumentFragment();

    sources.forEach((url, index) => {
      const figure = document.createElement('figure');
      figure.className = 'bp-lip-renderer__figure';
      const image = document.createElement('img');
      image.className = 'bp-lip-renderer__fallback';
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      const canvas = document.createElement('canvas');
      canvas.className = 'bp-lip-renderer__canvas';
      canvas.setAttribute('aria-hidden', 'true');
      canvas.hidden = true;
      const openButton = document.createElement('button');
      openButton.type = 'button';
      openButton.className = 'bp-lip-renderer__open';
      openButton.setAttribute('aria-label', `${enlargeText} ${labels[index] || ''}`.trim());
      const openIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      openIcon.setAttribute('viewBox', '0 0 24 24');
      openIcon.setAttribute('aria-hidden', 'true');
      openIcon.setAttribute('focusable', 'false');
      const openPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      openPath.setAttribute('d', 'M8 3H3v5 M16 3h5v5 M3 16v5h5 M21 16v5h-5');
      openPath.setAttribute('fill', 'none');
      openPath.setAttribute('stroke', 'currentColor');
      openPath.setAttribute('stroke-width', '2');
      openPath.setAttribute('stroke-linecap', 'round');
      openPath.setAttribute('stroke-linejoin', 'round');
      openIcon.append(openPath);
      openButton.append(openIcon);
      const caption = document.createElement('figcaption');
      caption.className = 'bp-lip-renderer__label';
      caption.textContent = labels[index] || '';
      figure.append(image, canvas, openButton, caption);
      fragment.append(figure);
      const photo = { url, image, canvas, figure, caption, openButton, ctx: null, source: null, original: null, area: null, mean: 0, loading: false, failed: false };
      photos.push(photo);
      openButton.addEventListener('click', () => {
        if (destroyed || !dialog.showModal) return;
        activePhoto = photo;
        returnFocus = openButton;
        dialog.setAttribute('aria-label', `${enlargeText} ${labels[index] || ''}`.trim());
        dialog.showModal();
        syncDialog();
        closeButton.focus();
      });
      image.onload = () => { if (photo.failed && !destroyed) image.hidden = false; };
      image.onerror = () => { image.hidden = true; };
      if (url) image.src = url;
      else { photo.failed = true; image.hidden = true; caption.textContent = `${labels[index] || ''} · ${unavailableText}`; figure.dataset.previewStatus = 'unavailable'; }
    });
    container.replaceChildren(fragment);

    const dialog = document.createElement('dialog');
    dialog.className = 'bp-lip-renderer__dialog';
    const dialogPanel = document.createElement('div');
    dialogPanel.className = 'bp-lip-renderer__dialog-panel';
    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'bp-lip-renderer__dialog-close';
    closeButton.setAttribute('aria-label', closeText);
    closeButton.textContent = '×';
    const dialogImage = document.createElement('img');
    dialogImage.className = 'bp-lip-renderer__dialog-fallback';
    dialogImage.alt = '';
    const dialogCanvas = document.createElement('canvas');
    dialogCanvas.className = 'bp-lip-renderer__dialog-image';
    dialogCanvas.setAttribute('aria-hidden', 'true');
    dialogCanvas.hidden = true;
    const dialogLabel = document.createElement('p');
    dialogLabel.className = 'bp-lip-renderer__dialog-label';
    dialogPanel.append(closeButton, dialogImage, dialogCanvas, dialogLabel);
    dialog.append(dialogPanel);
    container.append(dialog);
    const syncDialog = () => {
      if (!dialog.open || !activePhoto) return;
      dialogLabel.textContent = activePhoto.caption.textContent;
      if (activePhoto.source && !activePhoto.canvas.hidden) {
        dialogCanvas.width = activePhoto.canvas.width;
        dialogCanvas.height = activePhoto.canvas.height;
        dialogCanvas.getContext('2d')?.drawImage(activePhoto.canvas, 0, 0);
        dialogImage.hidden = true;
        dialogCanvas.hidden = false;
      } else {
        dialogCanvas.hidden = true;
        dialogImage.src = activePhoto.url || '';
        dialogImage.hidden = !activePhoto.url;
      }
    };
    closeButton.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
    dialog.addEventListener('keydown', event => {
      if (event.key !== 'Escape' || !dialog.open) return;
      event.preventDefault();
      dialog.close();
    });
    dialog.addEventListener('close', () => {
      activePhoto = null;
      dialogCanvas.width = 0;
      dialogCanvas.height = 0;
      if (returnFocus && !destroyed) returnFocus.focus({ preventScroll: true });
      returnFocus = null;
    });

    const showOriginal = photo => {
      photo.canvas.hidden = true;
      photo.image.hidden = photo.failed && !photo.image.naturalWidth;
    };
    const fail = photo => {
      photo.failed = true;
      photo.source = null;
      photo.original = null;
      photo.area = null;
      showOriginal(photo);
      photo.figure.dataset.previewStatus = 'unavailable';
      photo.caption.textContent = `${labels[photos.indexOf(photo)] || ''} · ${unavailableText}`;
      if (activePhoto === photo) syncDialog();
    };
    const schedule = () => {
      if (!destroyed && visible && !frame) frame = requestAnimationFrame(() => {
        frame = 0;
        photos.forEach(photo => {
          if (!photo.source) return;
          try {
            renderPhoto(photo, selection);
            photo.image.hidden = true;
            photo.canvas.hidden = false;
            photo.figure.dataset.previewStatus = 'ready';
            if (activePhoto === photo) syncDialog();
          } catch (error) { photo.figure.dataset.previewError = error?.message || 'render'; fail(photo); }
        });
      });
    };
    const load = photo => {
      if (destroyed || photo.loading || photo.source || photo.failed || !photo.url) return;
      photo.loading = true;
      const source = new Image();
      source.crossOrigin = 'anonymous';
      source.decoding = 'async';
      source.onload = () => {
        if (destroyed) return;
        try {
          const width = Math.min(MAX_SIZE, source.naturalWidth);
          const height = Math.min(MAX_SIZE, source.naturalHeight);
          if (!width || !height) throw Error('Empty image');
          photo.width = width;
          photo.height = height;
          photo.canvas.width = width;
          photo.canvas.height = height;
          photo.ctx = photo.canvas.getContext('2d', { willReadFrequently: true });
          if (!photo.ctx) throw Error('Canvas unavailable');
          photo.ctx.drawImage(source, 0, 0, width, height);
          photo.source = photo.ctx.getImageData(0, 0, width, height);
          photo.original = photo.source.data;
          photo.area = geometry(width, height);
          photo.mean = sourceMetrics(photo.original, width, photo.area);
          photo.loading = false;
          schedule();
        } catch {
          photo.loading = false;
          fail(photo);
        }
      };
      source.onerror = () => {
        photo.loading = false;
        if (!destroyed) fail(photo);
      };
      source.src = photo.url;
    };
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        visible = entries.some(entry => entry.isIntersecting);
        if (visible) {
          photos.forEach(load);
          schedule();
        }
      }, { rootMargin: '120px' });
      observer.observe(container);
    } else {
      visible = true;
      photos.forEach(load);
    }
    const api = {
      update(next = {}) {
        if (destroyed) return;
        const chosen = { base: next.base || null, liner: next.liner || null, finish: next.finish || null };
        const key = JSON.stringify([chosen.base?.id, chosen.base?.shade?.color, chosen.liner?.id, chosen.liner?.shade?.color, chosen.finish?.id, chosen.finish?.tint, chosen.finish?.sheen, chosen.finish?.glitter]);
        if (key === selectionKey) return;
        selectionKey = key;
        selection = chosen;
        photos.forEach(showOriginal);
        syncDialog();
        schedule();
      },
      destroy() {
        if (destroyed) return;
        destroyed = true;
        returnFocus = null;
        if (dialog.open) dialog.close();
        if (frame) cancelAnimationFrame(frame);
        observer?.disconnect();
        photos.forEach(photo => { photo.source = null; photo.original = null; photo.ctx = null; });
        container.replaceChildren();
        instances.delete(container);
      }
    };
    instances.set(container, { api });
    return api;
  }

  window.BPLipRenderer = { mount };
})();
